import { Box, Button, Link, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";

const COOKIE_NAME = "zube-cookie-consent";

export interface CookieConsentProps {
  onCookieStatusChange: (status: CookieAcceptanceState) => void;
  currentUserCookieStatus?: CookieAcceptanceState;
  isLoading: boolean;

}

export type CookieAcceptanceState = {
  essential: boolean;
  analytics: boolean | undefined;
  developerAid: boolean | undefined;
};

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2] ?? '') : null;
}

function setCookie(name: string, value: string, days = 365) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

interface CookieConsentBarProps {
  onAccept: () => void;
  onReject: () => void;
}

const CookieConsentBar: React.FC<CookieConsentBarProps> = ({ onAccept, onReject }) => (
  <Box
    sx={{
      position: "fixed",
      bottom: 0,
      left: 0,
      width: "100vw",
      height: "100vh",
      bgcolor: "rgba(40,40,40,0.45)",
      zIndex: 99999,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      pointerEvents: "auto",
      backdropFilter: "blur(2px)",
      transition: "background 0.3s",
      minHeight: 0,
      border: 0,
      overflow: "hidden", // Prevent scrolling inside the overlay
      touchAction: "none", // Prevent touch scrolling
      WebkitOverflowScrolling: "auto",
    }}
  >
    <Stack
      spacing={2}
      alignItems="center"
      sx={{
        maxWidth: 420,
        width: "90vw",
        bgcolor: "background.paper",
        boxShadow: 6,
        borderRadius: 3,
        p: 3,
        mb: 2,
        border: "1px solid #e0e0e0",
      }}
    >
      <Typography variant="h6" fontWeight={600} textAlign="center">
        🍪 Cookies help us help you!
      </Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">
        We use cookies to make the site work and to spot bugs 🐞 so we can fix them fast.
        <br />
        You choose if you want to help us by letting us track errors. No pressure!
        <br />
        <span role="img" aria-label="sparkles">✨</span>
        <Link
          href="https://forum.getzube.com/privacy/cookies"
          target="_blank"
          rel="noopener"
          sx={{ ml: 0.5, fontWeight: 500 }}
        >
          Learn more
        </Link>
      </Typography>
      <Stack direction="row" spacing={2} width="100%" justifyContent="center">
        <Button
          variant="contained"
          color="primary"
          onClick={onAccept}
          sx={{ minWidth: 100 }}
        >
          Yes, that&apos;s cool 👍
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={onReject}
          sx={{ minWidth: 100 }}
        >
          No thanks
        </Button>
      </Stack>
    </Stack>
  </Box>
);

export default function Cookie({ onCookieStatusChange, currentUserCookieStatus, isLoading }: CookieConsentProps) {
  const [cookieAcceptance, setCookieAcceptance] = useState<CookieAcceptanceState>({
    essential: false,
    analytics: undefined,
    developerAid: undefined,
  });

  const [showConsent, setShowConsent] = useState<boolean>(false);

  // If user is logged in, update acceptance state
  useEffect(() => {
    if (isLoading) return;
    const cookie = getCookie(COOKIE_NAME);
    const accepted = cookie === "accepted";
    const hasLocalDecision = cookie === "accepted" || cookie === "rejected";
    if (currentUserCookieStatus) {
      setCookieAcceptance({
        essential: true,
        analytics: currentUserCookieStatus.analytics || accepted,
        developerAid: currentUserCookieStatus.developerAid || accepted,
      });
      // If we have any backend preference recorded OR a local decision, do not show the consent UI
      setShowConsent(!hasLocalDecision && !currentUserCookieStatus);
    } else {
      // Show only if the user has not made any local decision yet
      setShowConsent(!hasLocalDecision);
    }
  }, [currentUserCookieStatus, isLoading]);

  // Enable analytics/devtools if accepted
  useEffect(() => {
    if (isLoading) return;
    if (cookieAcceptance.analytics === undefined || cookieAcceptance.developerAid === undefined) return;
    if (currentUserCookieStatus && (currentUserCookieStatus.analytics !== cookieAcceptance.analytics || currentUserCookieStatus.developerAid !== cookieAcceptance.developerAid)) {
      onCookieStatusChange(cookieAcceptance);
    }
  }, [cookieAcceptance, onCookieStatusChange, currentUserCookieStatus, isLoading]);

  const handleAccept = useCallback(() => {
    setCookie(COOKIE_NAME, "accepted");
    setCookieAcceptance({
      essential: true,
      analytics: true,
      developerAid: true,
    });
    setShowConsent(false);
  }, []);

  const handleReject = useCallback(() => {
    setCookie(COOKIE_NAME, "rejected");
    setCookieAcceptance({
      essential: true,
      analytics: false,
      developerAid: false,
    });
    setShowConsent(false);
  }, []);

  // Only show if not accepted or rejected
  if (!showConsent) return null;

  return (
    <CookieConsentBar onAccept={handleAccept} onReject={handleReject} />
  );
}